import type { RouteComponentProps } from '@reach/router';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import ViewTab from '@components/ViewTab';
import type { TabValue } from '@components/ViewTab/ViewTab';

import { useListAccounts } from '@hooks/api/account';

import { AccountType } from 'generated/types';

import AccountList from './List';

const messages = defineMessages({
  title: {
    id: 'accountsMain.title',
    defaultMessage: 'Accounts',
  },
  individual: {
    id: 'account.type.individual',
    defaultMessage: 'Individual',
  },
  organization: {
    id: 'account.type.organization',
    defaultMessage: 'Organization',
  },
});

const AccountsMain: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();

  // Fetched unconditionally (not just for the active tab) so both tab
  // counts are available together, same pattern already used by the
  // Client list (Patients/Non Patients).
  const { data: orgData = [], loading: orgLoading } = useListAccounts(
    AccountType.organization
  );
  const { data: individualData = [], loading: individualLoading } =
    useListAccounts(AccountType.individual);

  const tabs: TabValue<AccountType>[] = [
    {
      id: AccountType.organization,
      value: formatMessage(messages.organization),
      count: orgData.length,
    },
    {
      id: AccountType.individual,
      value: formatMessage(messages.individual),
      count: individualData.length,
    },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const loading = orgLoading || individualLoading;
  const data =
    activeTab.id === AccountType.organization ? orgData : individualData;

  const handleTabChange = async (value: TabValue<AccountType>) => {
    if (loading) {
      return;
    }
    setActiveTab(value);
  };

  return (
    <>
      <ViewTab
        changeTab={handleTabChange}
        selected={activeTab}
        tabArray={tabs}
      />

      <ContentLoader loading={loading}>
        <AccountList accountType={activeTab.id} data={data} />
      </ContentLoader>
    </>
  );
};

export default AccountsMain;
